import {ActionBar} from "@client/components/ActionBar";
import {TabBar, TabItem} from "@client/components/TabBar";
import {Toasts} from "@client/components/Toasts";
import {ActionDefinition, Locator} from "@client/gotterdammerung";
import * as analytics from "@client/shared/analytics";
import {useAppDispatch, useAppSelector} from "@client/store";
import * as style from "@client/style";
import {classNames} from "@reactivated";
import {
    MotionValue,
    animate,
    motion,
    useMotionValue,
    useMotionValueEvent,
    useScroll,
} from "motion/react";
import * as React from "react";

const styles = {
    topSection: "",
    scene: "flex flex-col flex-1 pb-0 w-full max-w-[900px] overflow-hidden",
    contentWrapper: "flex-1 relative",
    content: "overflow-auto [-webkit-overflow-scrolling:touch]",
    sceneBar: "",
    bottomSafeInset: `relative ${style.zIndexClasses.sceneBottom}`,
    bottom: "",
    isAnimating: "overflow-hidden",
} as const;

function Spinner() {
    return (
        <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
        >
            <g>
                <rect x="11" y="1" width="2" height="5" opacity=".14" />
                <rect
                    x="11"
                    y="1"
                    width="2"
                    height="5"
                    transform="rotate(30 12 12)"
                    opacity=".29"
                />
                <rect
                    x="11"
                    y="1"
                    width="2"
                    height="5"
                    transform="rotate(60 12 12)"
                    opacity=".43"
                />
                <rect
                    x="11"
                    y="1"
                    width="2"
                    height="5"
                    transform="rotate(90 12 12)"
                    opacity=".57"
                />
                <rect
                    x="11"
                    y="1"
                    width="2"
                    height="5"
                    transform="rotate(120 12 12)"
                    opacity=".71"
                />
                <rect
                    x="11"
                    y="1"
                    width="2"
                    height="5"
                    transform="rotate(150 12 12)"
                    opacity=".86"
                />
                <rect x="11" y="1" width="2" height="5" transform="rotate(180 12 12)" />
                <animateTransform
                    attributeName="transform"
                    type="rotate"
                    calcMode="discrete"
                    dur="0.75s"
                    values="0 12 12;30 12 12;60 12 12;90 12 12;120 12 12;150 12 12;180 12 12;210 12 12;240 12 12;270 12 12;300 12 12;330 12 12;360 12 12"
                    repeatCount="indefinite"
                />
            </g>
        </svg>
    );
}

const variants = {
    none: () => {
        return {
            opacity: 1,
        };
    },
    fade: () => {
        return {
            opacity: 0,
        };
    },
    slide: (reverseAnimation: boolean) => {
        return {
            x: reverseAnimation ? -1000 : 1000,
            opacity: 0,
        };
    },
    center: {
        zIndex: style.zIndexes.sceneContentAnimatingCenter,
        x: 0,
        opacity: 1,
    },
    exit: (reverseAnimation: boolean) => {
        return {
            zIndex: style.zIndexes.sceneContentAnimatingExit,
            x: reverseAnimation ? -1000 : 1000,
            opacity: 0,
        };
    },
};

export function useActions(actions: Array<ActionDefinition | false>) {
    const hasOutstandingAction = React.useRef(false);
    const dispatch = useAppDispatch();

    return actions.map((action) => {
        // Wrap all of our "remote" actions with a wrapper to monitor
        // loading progress.
        if (action == false) {
            return null;
        }

        if (action != null && action.type === "switch") {
            return {...action};
        } else if (action != null && action.onClick != null) {
            const {onClick} = action;

            return {
                ...action,
                type: "action" as const,
                onClick: async () => {
                    if (hasOutstandingAction.current === true) {
                        return;
                    }

                    hasOutstandingAction.current = true;
                    dispatch({
                        type: "ACTION_OUTSTANDING",
                    });

                    try {
                        await Promise.resolve(onClick());
                    } finally {
                        // TODO: technically, this could be split into
                        // ACTION_ERROR and ACTION_COMPLETED both with turn
                        // actions back on.
                        dispatch({
                            type: "ACTION_COMPLETED",
                        });
                        hasOutstandingAction.current = false;
                    }
                },
            };
        } else if (action != null && action.track != null) {
            const onClick =
                // oxlint-disable-next-line typescript/no-unnecessary-condition
                action.track != null
                    ? () =>
                          analytics.track("CLICKED_ACTION", {action_name: action.track})
                    : undefined;
            return {onClick, ...action, type: "action" as const};
        } else if (action != null) {
            return {...action, type: "action" as const};
        }
        return null;
    });
}

interface Props {
    actionsStyle?: "legacy" | "contrast";
    tabs: (TabItem | null)[];
    noBottomBorder?: boolean;
    actions: Array<ActionDefinition | false>;
    children: React.ReactNode;
    topSection?: React.ReactNode;
    bottomSection?: React.ReactNode;
    onRefresh?: () => Promise<unknown>;
    more?: React.ReactNode;
    locator: Locator | null;
    modal?: true;
    fadedScroll?: "FULL" | "FADE_ONLY";
}

const top = `0%`;
const bottom = `100%`;
const bottomInset = `80%`;
const transparent = `#0000`;
const opaque = `#fff`;

function useScrollOverflowMask(scrollYProgress: MotionValue<number>) {
    const maskImage = useMotionValue(
        `linear-gradient(180deg, ${opaque}, ${opaque} ${top}, ${opaque} ${bottomInset}, ${opaque} 85%, ${transparent})`,
    );

    useMotionValueEvent(scrollYProgress, "change", (value) => {
        if (value === 0) {
            animate(
                maskImage,
                `linear-gradient(180deg, ${opaque}, ${opaque} ${top}, ${opaque} ${bottomInset}, ${opaque} 85%, ${transparent})`,
            );
        } else if (value >= 0.999) {
            // More than to capture iOS momentum scrolling down.
            // Using 0.999 instead of 1 to handle floating point precision issues
            // Say with height auto elements like SVGs and images, etc.
            animate(
                maskImage,
                `linear-gradient(180deg, ${opaque}, ${opaque} ${top}, ${opaque} ${bottom}, ${opaque})`,
            );
        } else if (
            scrollYProgress.getPrevious() === 0 ||
            scrollYProgress.getPrevious() === 1
        ) {
            animate(
                maskImage,
                `linear-gradient(180deg, ${opaque}, ${opaque} ${top}, ${opaque} ${bottomInset}, ${opaque} 85%, ${transparent})`,
            );
        }
    });

    return maskImage;
}

export const SceneContext = React.createContext<{
    scrollRef: React.RefObject<HTMLDivElement | null>;
    scrollYProgress: MotionValue<number>;
    clampedProgress: MotionValue<number>;
    maskImage: MotionValue<string>;
    isRefreshing: boolean;
    setIsRefreshing: React.Dispatch<React.SetStateAction<boolean>>;
}>(null!);

export const Scene = (props: Props) => {
    const dispatch = useAppDispatch();
    const startYRef = React.useRef<number | null>(null);
    const [isRefreshing, setIsRefreshing] = React.useState(false);
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const {scrollYProgress} = useScroll({container: scrollRef});
    const clampedProgress = useMotionValue(0);
    const maskImage = useScrollOverflowMask(scrollYProgress);

    const isScanning = useAppSelector((state) => state.search.isScanning);
    const nextAnimation = useAppSelector((state) => state.scenes.nextAnimation);
    const platform = useAppSelector((state) => state.boot.platform)!;
    const isAppScreens = useAppSelector((state) => state.scenes.isAppScreens);

    React.useEffect(() => {
        if (nextAnimation != "disabled") {
            dispatch({type: "SET_NEXT_ANIMATION", animation: "slide"});
        }
    }, []);

    React.useEffect(() => {
        const unsubscribe = scrollYProgress.on("change", (latest) => {
            // Simply clamp the progress between 0 and 1
            clampedProgress.set(Math.max(0, Math.min(1, latest)));
        });
        return () => unsubscribe();
    }, [scrollYProgress]);

    const actions = useActions(props.actions);

    const handleTouchStart = React.useCallback((e: React.TouchEvent) => {
        if (platform !== "ios" || props.onRefresh == null) {
            return;
        }
        startYRef.current = e.touches[0].pageY;
    }, []);

    const handleTouchMove = React.useCallback(
        async (e: React.TouchEvent) => {
            if (platform !== "ios" || props.onRefresh == null) {
                return;
            }
            if (startYRef.current == null) return;

            const y = e.touches[0].pageY;
            const scroller = document.getElementById("app-scroller");

            // Check if we're at the top and pulling down
            if (
                scroller &&
                scroller.scrollTop === 0 &&
                y > startYRef.current &&
                !isRefreshing
            ) {
                setIsRefreshing(true);
                await props.onRefresh();
            }
        },
        [isRefreshing],
    );

    const handleTouchEnd = React.useCallback(() => {
        if (platform !== "ios" || props.onRefresh == null) {
            return;
        }
        setIsRefreshing(false);
    }, []);
    // https://jsfiddle.net/n78dg9w5/15/

    const content = (
        <div className={classNames(styles.scene)} key={props.locator?.scenePath}>
            <div className={classNames(styles.topSection, "relative bg-stone-50")}>
                {props.topSection != null ? (
                    <div
                        style={{
                            padding: "0 10px 0 10px",
                        }}
                    >
                        {props.topSection}
                    </div>
                ) : (
                    <div className="-mt-2"></div>
                )}
                <TabBar tabs={props.tabs} />

                {isRefreshing && (
                    <div className="absolute inset-x-0 top-0 z-10 flex justify-center">
                        <Spinner />
                    </div>
                )}
            </div>
            <div
                className={classNames(
                    styles.contentWrapper,
                    isScanning == false && "bg-stone-50",
                    isScanning && props.modal == null && "invisible",
                )}
            >
                {props.fadedScroll === "FULL" && (
                    <div className="absolute right-2 bottom-2.5 z-10 flex items-center gap-2">
                        <svg className="size-8 -rotate-90" viewBox="0 0 100 100">
                            <circle
                                cx="50"
                                cy="50"
                                r="30"
                                pathLength="1"
                                className="stroke-gray-200"
                                fill="none"
                                strokeWidth="10%"
                            />
                            <motion.circle
                                cx="50"
                                cy="50"
                                r="30"
                                className="stroke-blue-500"
                                fill="none"
                                strokeWidth="10%"
                                style={{pathLength: clampedProgress}}
                            />
                        </svg>
                    </div>
                )}
                <motion.div
                    ref={scrollRef}
                    key={props.locator?.scenePath}
                    id="app-scroller"
                    className={classNames(
                        styles.content,
                        "absolute inset-0 overflow-auto p-4",
                        platform === "ios" &&
                            "overscroll-contain before:bg-stone-50 before:h-px before:w-full before:block",
                        // Top sections currently have a border, so we only pull up on basically the journal.
                        platform === "ios" && props.topSection == null && "-mt-px",
                        platform === "ios" && isRefreshing && "*:bg-stone-50",
                    )}
                    style={props.fadedScroll != null ? {maskImage} : undefined}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    custom={false}
                    variants={variants}
                    initial={nextAnimation}
                    animate="center"
                    exit="exit"
                    transition={{
                        x: {type: "spring", stiffness: 300, damping: 32},
                        opacity: {duration: 0.2},
                    }}
                >
                    {props.children}
                </motion.div>
            </div>
            <div
                className={classNames(
                    styles.bottomSafeInset,
                    "bg-stone-50",
                    isAppScreens &&
                        "pb-safe-only absolute bottom-[6.55rem] left-0 right-0",
                    "pb-safe",
                )}
            >
                <Toasts />
                <div
                    className={classNames(
                        styles.bottom,
                        props.actionsStyle != "contrast" &&
                            props.noBottomBorder != true &&
                            classNames(
                                "border-t border-gray-300 empty:border-t-0",
                                "sm:border-l sm:border-r",
                                // platform == "ios" && "border-b empty:border-b-0",
                                "border-b empty:border-b-0",
                            ),
                    )}
                >
                    {props.bottomSection}
                    <ActionBar
                        actionsStyle={props.actionsStyle}
                        actions={props.more == null ? actions : []}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <SceneContext
            value={{
                scrollRef,
                scrollYProgress,
                clampedProgress,
                maskImage,
                isRefreshing,
                setIsRefreshing,
            }}
        >
            {content}
        </SceneContext>
    );
};
